/**
 * Cartero mail REST client (raw `fetch`).
 *
 * The mail endpoints (`${SERVER_URL}/api/mail/*`) are NOT part of the
 * OpenAPI-generated `@cartero/client` types, so we call them with a plain
 * `fetch` that mirrors the app's cookie-BFF auth transport:
 *
 *   - `credentials: "include"` → the HttpOnly `cartero.device` cookie rides the
 *     request (web) so the backend can resolve the session.
 *   - `X-Active-Account: <userId>` → the per-tab active-account selector so the
 *     backend picks THIS tab's session (see `lib/accounts.ts`).
 *
 * All request/response bodies are camelCase JSON. This module is the single
 * fetch layer + query-key factory for the whole mail feature; the reactive
 * hooks live in `hooks.ts`.
 */

import { SERVER_BASE_URL } from "@/lib/api";
import { readActiveAccountSelector } from "@/lib/accounts";

// ─── IMAP system flags ───────────────────────────────────────────────────────
// RFC 3501 §2.3.2 protocol tokens. These are wire literals the IMAP server
// matches verbatim — translating them would break flag store/fetch, so they are
// intentionally exempt from i18n.
export const IMAP_FLAG_SEEN = String.raw`\Seen`;
export const IMAP_FLAG_FLAGGED = String.raw`\Flagged`;

// ─── Domain types (mirror the backend contract) ─────────────────────────────

export interface MailAccountView {
  id: string;
  /** Set when this is an org team mailbox shared with every member. */
  orgId?: string | null;
  email: string;
  displayName?: string | null;
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
  username?: string | null;
  lastSynced?: string | null;
  createdAt: string;
}

export interface MailFolder {
  name: string;
  total: number;
  unread: number;
}

export interface MessageSummary {
  uid: number;
  folder: string;
  messageId: string;
  subject: string;
  from: string;
  to: string[];
  date: string;
  snippet: string;
  seen: boolean;
  flagged: boolean;
  hasAttachments: boolean;
}

export interface MessageAttachment {
  filename: string;
  contentType: string;
  size: number;
}

export interface MessageDetail {
  uid: number;
  folder: string;
  messageId: string;
  inReplyTo?: string | null;
  references: string[];
  subject: string;
  from: string;
  to: string[];
  date: string;
  textBody?: string | null;
  htmlBody?: string | null;
  attachments: MessageAttachment[];
  seen: boolean;
  flagged: boolean;
  isEncrypted: boolean;
}

export interface MailThread {
  subject: string;
  messages: MessageSummary[];
  unread: boolean;
}

export interface SyncResult {
  folder: string;
  fullResync: boolean;
  newMessages: number;
  flagUpdates: number;
  expunged: number;
}

export type RuleField = "from" | "to" | "subject" | "body";
export type RuleOp = "contains" | "equals" | "starts_with" | "ends_with";
export type RuleActionType = "move" | "add_label" | "add_flag" | "mark_read" | "delete";

export interface RuleCondition {
  field: RuleField;
  op: RuleOp;
  value: string;
}

export interface RuleAction {
  type: RuleActionType;
  value?: string;
}

export interface MailRule {
  name: string;
  matchKind: "all" | "any";
  conditions: RuleCondition[];
  actions: RuleAction[];
  stop: boolean;
}

export interface ConnectAccountInput {
  email: string;
  displayName?: string;
  imapHost: string;
  imapPort: number;
  imapTls?: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpTls?: boolean;
  username?: string;
  password: string;
  /**
   * Connect as a team mailbox of this organization (requires org owner/admin);
   * every member can then read and send through it.
   */
  orgId?: string;
}

/**
 * One row of the mailbox access-audit trail (activity-core reuse): who used the
 * account, when, and what for (`patch.event`: connected/synced/exported_eml/
 * exported_mbox/sent/rules_updated/removed).
 */
export interface MailAuditEntry {
  id: string;
  entityId: string;
  action: string;
  actorUserId: string;
  actorName?: string | null;
  serverTs: string;
  patch: { event?: string } & Record<string, unknown>;
}

export interface SendMessageInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  text?: string;
  html?: string;
  inReplyTo?: string;
  references?: string[];
  e2ePublicKey?: string;
}

export interface FlagsInput {
  folder: string;
  add?: string[];
  remove?: string[];
}

export interface MoveInput {
  folder: string;
  dest: string;
}

interface E2eKeyView {
  publicKey: string;
}

// OAuth mailbox connect (Gmail / Outlook via XOAUTH2). The server builds the
// consent URL (start) and handles the provider callback itself; the web only
// redirects the browser and reads the `?oauth=` outcome on /mail/settings.
export type MailOAuthProvider = "gmail" | "outlook";

interface MailOAuthStatus {
  gmail: boolean;
  outlook: boolean;
}

export interface MailOAuthStart {
  url: string;
}

// ─── Fetch transport ────────────────────────────────────────────────────────

const MAIL_BASE = `${SERVER_BASE_URL.replace(/\/$/, "")}/api/mail`;

/** Builds the cookie-BFF auth headers (active-account selector) for mail calls. */
function mailAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const selector = readActiveAccountSelector();
  if (selector) {
    headers["X-Active-Account"] = selector;
  }
  return headers;
}

/**
 * Core mail fetch. Attaches the device cookie (`credentials: "include"`) + the
 * active-account selector, sends/receives JSON, and throws an `Error` carrying
 * `status` on a non-2xx so TanStack Query surfaces it. A 204 / empty body
 * resolves to `undefined`.
 */
export async function mailFetch<T>(
  path: string,
  init?: Omit<RequestInit, "headers"> & { json?: unknown; headers?: Record<string, string> },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...mailAuthHeaders(),
    ...headers,
  };
  if (json !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(`${MAIL_BASE}${path}`, {
    credentials: "include",
    ...rest,
    headers: finalHeaders,
    body: json === undefined ? rest.body : JSON.stringify(json),
  });

  if (!response.ok) {
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- internal diagnostic Error message; user-facing mail errors are localized at the toast layer
    let detail = `Mail request failed (${response.status})`;
    try {
      const body: unknown = await response.json();
      if (body && typeof body === "object") {
        const msg =
          ("detail" in body && typeof body.detail === "string" && body.detail) ||
          ("message" in body && typeof body.message === "string" && body.message) ||
          ("error" in body && typeof body.error === "string" && body.error);
        if (msg) {
          detail = msg;
        }
      }
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    const err = new Error(detail);
    Object.assign(err, { status: response.status });
    throw err;
  }

  if (response.status === 204) {
    // Trust boundary: a 204 carries no body; the caller's `T` is `void`.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return undefined as T;
  }
  // Trust-boundary cast of the server's JSON to the endpoint's declared shape.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return JSON.parse(text) as T;
}

/**
 * Binary variant of {@link mailFetch} for the export endpoints (raw `.eml` /
 * `.mbox`): same cookie-BFF + active-account auth, but resolves the response as
 * a `Blob` (the body is raw RFC822 / mbox bytes, not JSON). Non-2xx throws an
 * `Error` carrying `status`, exactly like `mailFetch`.
 */
export async function mailFetchBlob(path: string): Promise<Blob> {
  const response = await fetch(`${MAIL_BASE}${path}`, {
    credentials: "include",
    headers: mailAuthHeaders(),
  });
  if (!response.ok) {
    const err = new Error(`Mail export failed (${response.status})`);
    Object.assign(err, { status: response.status });
    throw err;
  }
  return response.blob();
}

// ─── Endpoint functions ──────────────────────────────────────────────────────

export const mailApi = {
  listAccounts: () => mailFetch<MailAccountView[]>("/accounts"),
  connectAccount: (body: ConnectAccountInput) =>
    mailFetch<MailAccountView>("/accounts", { method: "POST", json: body }),
  deleteAccount: (id: string) =>
    mailFetch<void>(`/accounts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  syncAccount: (id: string) =>
    mailFetch<SyncResult[]>(`/accounts/${encodeURIComponent(id)}/sync`, { method: "POST" }),
  listFolders: (id: string) =>
    mailFetch<MailFolder[]>(`/accounts/${encodeURIComponent(id)}/folders`),
  listMessages: (id: string, folder: string) =>
    mailFetch<MessageSummary[]>(
      `/accounts/${encodeURIComponent(id)}/messages?folder=${encodeURIComponent(folder)}`,
    ),
  getMessage: (id: string, uid: number, folder: string) =>
    mailFetch<MessageDetail>(
      `/accounts/${encodeURIComponent(id)}/messages/${uid}?folder=${encodeURIComponent(folder)}`,
    ),
  /** Raw RFC822 bytes of one message → a faithful `.eml` download. */
  getRawMessage: (id: string, uid: number, folder: string) =>
    mailFetchBlob(
      `/accounts/${encodeURIComponent(id)}/messages/${uid}/raw?folder=${encodeURIComponent(folder)}`,
    ),
  /** A whole folder as an mbox → a faithful `.mbox` download. */
  exportFolderMbox: (id: string, folder: string) =>
    mailFetchBlob(
      `/accounts/${encodeURIComponent(id)}/export?folder=${encodeURIComponent(folder)}`,
    ),
  listThreads: (id: string, folder: string) =>
    mailFetch<MailThread[]>(
      `/accounts/${encodeURIComponent(id)}/threads?folder=${encodeURIComponent(folder)}`,
    ),
  search: (id: string, q: string) =>
    mailFetch<MessageSummary[]>(
      `/accounts/${encodeURIComponent(id)}/search?q=${encodeURIComponent(q)}`,
    ),
  send: (id: string, body: SendMessageInput) =>
    mailFetch<void>(`/accounts/${encodeURIComponent(id)}/send`, { method: "POST", json: body }),
  saveDraft: (id: string, body: SendMessageInput) =>
    mailFetch<void>(`/accounts/${encodeURIComponent(id)}/drafts`, { method: "POST", json: body }),
  setFlags: (id: string, uid: number, body: FlagsInput) =>
    mailFetch<void>(`/accounts/${encodeURIComponent(id)}/messages/${uid}/flags`, {
      method: "POST",
      json: body,
    }),
  move: (id: string, uid: number, body: MoveInput) =>
    mailFetch<void>(`/accounts/${encodeURIComponent(id)}/messages/${uid}/move`, {
      method: "POST",
      json: body,
    }),
  /** The account's access-audit trail, newest first. */
  getAudit: (id: string, limit?: number) =>
    mailFetch<MailAuditEntry[]>(
      limit
        ? `/accounts/${encodeURIComponent(id)}/audit?limit=${limit}`
        : `/accounts/${encodeURIComponent(id)}/audit`,
    ),
  getRules: (id: string) => mailFetch<MailRule[]>(`/accounts/${encodeURIComponent(id)}/rules`),
  putRules: (id: string, rules: MailRule[]) =>
    mailFetch<MailRule[]>(`/accounts/${encodeURIComponent(id)}/rules`, {
      method: "PUT",
      json: rules,
    }),
  publishE2eKey: (publicKey: string) =>
    mailFetch<void>("/e2e/key", { method: "POST", json: { publicKey } }),
  lookupE2eKey: (email: string) =>
    mailFetch<E2eKeyView>(`/e2e/key?email=${encodeURIComponent(email)}`),
  /** Which OAuth providers the server has credentials for (drives the buttons). */
  oauthProviders: () => mailFetch<MailOAuthStatus>("/oauth/providers"),
  /**
   * Mint the provider consent URL. The response also sets the HttpOnly flow
   * cookie (state + PKCE + user pinned server-side); the caller must then
   * navigate the browser to `url`.
   */
  oauthStart: (provider: MailOAuthProvider) =>
    mailFetch<MailOAuthStart>(`/oauth/${provider}/start`, { method: "POST" }),
};

// ─── Query key factory (mirrors the repo's `queryKeys` pattern) ──────────────

export const mailKeys = {
  all: ["mail"] as const,
  accounts: () => ["mail", "accounts"] as const,
  oauthProviders: () => ["mail", "oauth-providers"] as const,
  folders: (accountId: string | undefined) => ["mail", "folders", accountId] as const,
  messages: (accountId: string | undefined, folder: string) =>
    ["mail", "messages", accountId, folder] as const,
  /** Prefix key for invalidating a whole account's messages across all folders. */
  messagesByAccount: (accountId: string) => ["mail", "messages", accountId] as const,
  message: (accountId: string | undefined, folder: string, uid: number | undefined) =>
    ["mail", "message", accountId, folder, uid] as const,
  threads: (accountId: string | undefined, folder: string) =>
    ["mail", "threads", accountId, folder] as const,
  /** Prefix key for invalidating a whole account's threads across all folders. */
  threadsByAccount: (accountId: string) => ["mail", "threads", accountId] as const,
  search: (accountId: string | undefined, q: string) => ["mail", "search", accountId, q] as const,
  rules: (accountId: string | undefined) => ["mail", "rules", accountId] as const,
  audit: (accountId: string | undefined) => ["mail", "audit", accountId] as const,
};
